import axios from 'axios';
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { CONFIG } from 'variables/config';
import { allChannels } from 'variables/industries';

interface IndustryOption {
    name: string;
    subIndustries?: { name: string }[];
}

interface Option {
    value: string;
    label: string;
    sublabel?: string;
}

const SearchAddChips = ({
    label,
    placeholder,
    value,
    onChange,
    options,
}: {
    label: string;
    placeholder: string;
    value: string[];
    onChange: (v: string[]) => void;
    options: Option[];
}) => {
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);

    const filtered = options
        .filter((o) => !value.includes(o.value))
        .filter(
            (o) =>
                !search ||
                o.label.toLowerCase().includes(search.toLowerCase()) ||
                o.sublabel?.toLowerCase().includes(search.toLowerCase())
        );

    const add = (v: string) => {
        onChange([...value, v]);
        setSearch('');
        setOpen(false);
    };

    const remove = (v: string) => onChange(value.filter((x) => x !== v));

    return (
        <div>
            <label className="ml-1 font-geist text-xs font-semibold text-leadrs-text-soft">{label}</label>
            <div className="relative mt-1.5">
                <input
                    type="text"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    placeholder={placeholder}
                    className="w-full rounded-lg border border-leadrs-stroke bg-white px-3 py-2 font-geist text-sm text-leadrs-text outline-none placeholder:text-leadrs-text-soft/60 focus:border-leadrs-brand"
                />
                {open && filtered.length > 0 && (
                    <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-leadrs-stroke bg-white shadow-lg">
                        {filtered.map((o) => (
                            <div
                                key={o.value}
                                onMouseDown={() => add(o.value)}
                                className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 font-geist text-sm text-leadrs-text hover:bg-leadrs-bg"
                            >
                                <span>{o.label}</span>
                                {o.sublabel && (
                                    <span className="font-geist text-xs text-leadrs-text-soft">{o.sublabel}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {value.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                    {value.map((v) => {
                        const opt = options.find((o) => o.value === v);
                        return (
                            <span
                                key={v}
                                className="inline-flex items-center gap-1 rounded-full border border-leadrs-purple-focus/30 bg-leadrs-purple-soft px-2.5 py-1 font-geist text-xs font-medium text-leadrs-brand"
                            >
                                {opt?.label ?? v}
                                <button
                                    type="button"
                                    onClick={() => remove(v)}
                                    className="text-leadrs-brand/60 hover:text-leadrs-brand"
                                >
                                    ×
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const AcquisitionPreferences = () => {
    const [industries, setIndustries] = useState<IndustryOption[]>([]);
    const [subIndustries, setSubIndustries] = useState<string[]>([]);
    const [channels, setChannels] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const authConfig = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [userRes, industriesRes] = await Promise.all([
                    axios.get(`${CONFIG.apiUrl}/user/me`, authConfig()),
                    axios.get(`${CONFIG.apiUrl}/industry`),
                ]);
                setSubIndustries(userRes.data.subIndustries || []);
                setChannels(userRes.data.channels || []);
                setIndustries(industriesRes.data || []);
            } catch (err) {
                console.log(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const subIndustryOptions: Option[] = useMemo(
        () =>
            industries.flatMap((i) =>
                (i.subIndustries || []).map((s) => ({ value: s.name, label: s.name, sublabel: i.name }))
            ),
        [industries]
    );

    const channelOptions: Option[] = useMemo(
        () => allChannels.map((c) => ({ value: c, label: c })),
        []
    );

    const save = () => {
        setSaving(true);
        axios
            .put(`${CONFIG.apiUrl}/user`, { subIndustries, channels }, authConfig())
            .then(() => toast.success('Vos préférences ont été mises à jour'))
            .catch((err) => {
                console.log(err);
                toast.error("Une erreur est survenue lors de l'enregistrement");
            })
            .finally(() => setSaving(false));
    };

    if (loading) {
        return (
            <div className="rounded-xl border border-leadrs-stroke bg-white px-4 py-4">
                <p className="font-geist text-sm text-leadrs-text-soft">Chargement...</p>
            </div>
        );
    }

    return (
        <div className="rounded-xl border border-leadrs-stroke bg-white px-4 py-4">
            <h4 className="text-base font-bold text-leadrs-text">
                Secteurs et canaux d'acquisition
            </h4>

            <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <SearchAddChips
                    label="Sous-secteurs"
                    placeholder="Rechercher un sous-secteur..."
                    value={subIndustries}
                    onChange={setSubIndustries}
                    options={subIndustryOptions}
                />
                <SearchAddChips
                    label="Canaux d'acquisition"
                    placeholder="Rechercher un canal..."
                    value={channels}
                    onChange={setChannels}
                    options={channelOptions}
                />
            </div>

            <div className="mt-4 flex w-full justify-end">
                <button
                    className="linear rounded-lg bg-leadrs-brand px-4 py-1.5 text-sm font-medium text-white transition duration-200 hover:cursor-pointer disabled:opacity-50"
                    onClick={save}
                    disabled={saving}
                >
                    {saving ? 'Enregistrement...' : 'Sauvegarder'}
                </button>
            </div>
        </div>
    );
};

export default AcquisitionPreferences;
